import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags } from '@/features/shared/tags';
import { TableActionsCell, TableCellContainer } from '@/features/tests/components/testsTableCells';
import { mapChipKeyToStatusTone } from '@/features/tests/components/testsTableShared';
import {
  Box,
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  getStatusTextColor,
  Link,
  resolveStatusChipTone,
  Text,
  Tooltip,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import {
  getStatusIconComponent,
  getThresholdChipKey,
  TableNameCell,
  TableTextCell,
} from '@/ui/composites/table';
import {
  mapSuiteExecutionRunSummaries,
  mapTestExecutionSummaries,
} from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  RELATIVE_TIME_TEXT_SX,
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from './suitesColumns.latestCellStyles';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { MetricThresholdGroup, SuiteExecutionStatus, SuiteListItemDto } from '@flowqa/shared';

type SuitesColumnsOptions = {
  includeLatestTestsColumn?: boolean;
  includeLastExecutedColumn?: boolean;
  latestAsMeta?: boolean;
  includeTagsInNameColumn?: boolean;
  includeActionsColumn?: boolean;
  onDeleteRow?: (row: SuiteListItemDto) => void;
  deleteRowPendingId?: string | null;
  tagColorByName?: Record<string, TagColor>;
  onUpdateRowTagsColor?: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId?: string | null;
  metricSeriesBySuiteId?: Map<
    string,
    {
      dates: Date[];
      passRate: number[];
      frequencyPerDay: number[];
      averageDurationMs: number[];
    }
  >;
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  showDeltas?: boolean;
  disableLinks?: boolean;
};

const columnWidths = tableColumnWidths.suites;

export const buildSuitesColumns = ({
  includeLatestTestsColumn = true,
  includeLastExecutedColumn = true,
  latestAsMeta = false,
  includeTagsInNameColumn = false,
  includeActionsColumn = false,
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
  metricSeriesBySuiteId: _metricSeriesBySuiteId,
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showDeltas = true,
  disableLinks = false,
}: SuitesColumnsOptions): Array<GridColDef<SuiteListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: SuiteListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const resolveTagTone = (tag: string): TagColor => {
    const trimmed = tag.trim();
    const fallback = trimmed.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(tagColorByName, trimmed)) {
      return tagColorByName[trimmed];
    }
    if (Object.prototype.hasOwnProperty.call(tagColorByName, fallback)) {
      return tagColorByName[fallback];
    }
    return 'neutral';
  };
  const nameColumn: GridColDef<SuiteListItemDto> = {
    field: 'name',
    headerName: 'Suite',
    flex: columnWidths.name.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'table-name-cell' : undefined,
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      if (latestAsMeta) {
        const tags = resolveEntityTags(params.row);
        const suiteHref = disableLinks ? null : `/suites/${params.row.slug}`;
        const resolvedTags = includeTagsInNameColumn
          ? tags.map((tag) => ({ label: tag, tone: resolveTagTone(tag) }))
          : [];
        return (
          <TableCellContainer gap="none">
            <TableNameCell
              primary={{
                label: params.row.name,
                ...(suiteHref
                  ? {
                      href: suiteHref,
                      onClick: () => {
                        onProjectSelect(params.row.projectId);
                      },
                    }
                  : {}),
              }}
              tags={resolvedTags}
            />
          </TableCellContainer>
        );
      }
      return (
        <TableTextCell
          primary={{
            label: params.row.name,
            ...(disableLinks
              ? {}
              : {
                  href: `/suites/${params.row.slug}`,
                  onClick: () => {
                    onProjectSelect(params.row.projectId);
                  },
                }),
          }}
        />
      );
    },
  };

  const latestSummaryColumn: GridColDef<SuiteListItemDto> = {
    field: 'latestExecutionStatus',
    headerName: 'Latest Status',
    flex: columnWidths.latestStatus.flex,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'table-latest-meta-cell' : undefined,
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const isLoadingRow = (params.row as { __loading?: boolean }).__loading === true;
      if (isLoadingRow) {
        return null;
      }
      const latestExecution = params.row.latestExecution;
      const status = latestExecution?.status ?? null;
      if (latestAsMeta) {
        const statusLabel = formatStatusLabel(status) || '-';
        const statusTone = resolveStatusChipTone(status);
        const durationLabel =
          latestExecution?.durationMs !== null && latestExecution?.durationMs !== undefined
            ? formatDuration(latestExecution.durationMs)
            : '';
        const timestamp = latestExecution?.timestamp ?? null;
        const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
        const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
        const resolvedTimestampTitle =
          timestampTitle || (timestamp instanceof Date ? timestamp.toISOString() : timestamp);
        const runNumber = latestExecution?.runNumber ?? null;
        const executionHref =
          runNumber !== null && params.row.slug && !disableLinks
            ? `/suites/${params.row.slug}/runs/${runNumber}`
            : null;
        return (
          <TableCellContainer gap="none">
            <TableStatusMetaContent
              statusLabel={statusLabel}
              statusHref={executionHref}
              onStatusClick={(event) => {
                event.stopPropagation();
                onProjectSelect(params.row.projectId);
              }}
              chipTone={statusTone}
              chipIcon={<Box component={getStatusIconComponent(status)} />}
              chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
              chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
              durationLabel={durationLabel}
              durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
              timestampLabel={timestampLabel}
              timestampTitle={resolvedTimestampTitle}
              timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            />
          </TableCellContainer>
        );
      }
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'Untested';
      const runNumber = latestExecution?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/suites/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };
  const lastExecutedColumn: GridColDef<SuiteListItemDto, Date | string | null> = {
    field: 'latestExecutionTimestamp',
    headerName: 'Last Executed',
    flex: columnWidths.lastExecuted.flex,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    valueGetter: (_value, row) => row.latestExecution?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto, Date | string | null>) => {
      const timestamp = params.value ?? null;
      const label = timestamp ? formatRelativeTime(timestamp) : '-';
      const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      return (
        <Tooltip
          title={fullTimestamp}
          disableHoverListener={!fullTimestamp}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '15px',
                lineHeight: 1.4,
              },
            },
          }}
        >
          <Box component="span" sx={{ display: 'block', width: '100%' }}>
            <Text role="body" component="span" sx={RELATIVE_TIME_TEXT_SX}>
              {label.length > 0 ? label : '-'}
            </Text>
          </Box>
        </Tooltip>
      );
    },
  };

  const testsColumn: GridColDef<SuiteListItemDto> = {
    field: 'testLatestExecutionBreakdown',
    headerName: 'Latest Tests',
    flex: latestAsMeta ? columnWidths.latestTests.flex : 0.9,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const passed = counts?.passed ?? 0;
      const failed = counts?.failed ?? 0;
      const errored = counts?.errored ?? 0;
      const skipped = counts?.skipped ?? 0;
      const untested = counts?.untested ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="test"
            counts={{ passed, failed, errored, skipped, untested }}
            summaryItems={mapTestExecutionSummaries(params.row.latestTestResults?.summaries)}
          />
        </TableCellContainer>
      );
    },
  };

  const executionsColumn: GridColDef<SuiteListItemDto> = {
    field: 'executionStatusCounts',
    headerName: 'Executions',
    flex: columnWidths.executions.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      const counts = params.row.executionStatusCounts;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="execution"
            counts={{
              passed: counts?.passed ?? 0,
              failed: counts?.failed ?? 0,
              errored: counts?.errored ?? 0,
              skipped: counts?.skipped ?? 0,
              untested: counts?.untested ?? 0,
            }}
            summaryItems={mapSuiteExecutionRunSummaries(params.row.executionSummaries)}
            summaryUnitLabel="suite"
            totalLinkMode="execution"
          />
        </TableCellContainer>
      );
    },
  };

  const newMetricsColumn: GridColDef<SuiteListItemDto> = {
    field: 'metricsSummary',
    headerName: 'Metrics',
    flex: columnWidths.metrics.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const resolvedPassRate = passRate;
      const resolvedPassRateDelta = resolvedPassRate === null ? null : passRateDelta;
      const resolvedFrequency = frequency;
      const resolvedFrequencyDelta = resolvedFrequency === null ? null : frequencyDelta;
      const resolvedAvgDuration = avgDuration;
      const resolvedAvgDurationDelta = resolvedAvgDuration === null ? null : avgDurationDelta;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedPassRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFrequency, thresholdGroup.frequency),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedAvgDuration, thresholdGroup.avgDuration),
      );

      const passRateLabel =
        resolvedPassRate === null ? 'No data' : `${formatPercent(resolvedPassRate)}%`;
      const passRateDeltaLabel =
        resolvedPassRateDelta === null
          ? null
          : resolvedPassRateDelta === 0
            ? 'No change'
            : `${resolvedPassRateDelta > 0 ? '+' : ''}${formatPercent(resolvedPassRateDelta)}%`;
      const frequencyLabel =
        resolvedFrequency === null ? formatFrequency(0) : formatFrequency(resolvedFrequency);
      const frequencyDeltaLabel =
        resolvedFrequencyDelta === null
          ? null
          : resolvedFrequencyDelta === 0
            ? 'No change'
            : `${resolvedFrequencyDelta > 0 ? '+' : resolvedFrequencyDelta < 0 ? '-' : ''}${formatFrequency(
                Math.abs(resolvedFrequencyDelta),
              )}`;
      const avgDurationLabel =
        resolvedAvgDuration === null ? 'No data' : formatDuration(resolvedAvgDuration);
      const avgDurationDeltaLabel =
        resolvedAvgDurationDelta === null
          ? null
          : resolvedAvgDurationDelta === 0
            ? 'No change'
            : `${resolvedAvgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                Math.abs(resolvedAvgDurationDelta),
                { roundMillisecondsWithSeconds: true },
              )}`;

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: 'Pass Rate',
                valueLabel: passRateLabel,
                deltaValue: resolvedPassRateDelta,
                deltaLabel: passRateDeltaLabel,
                isMissing: resolvedPassRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: 'Frequency',
                valueLabel: frequencyLabel,
                deltaValue: resolvedFrequencyDelta,
                deltaLabel: frequencyDeltaLabel,
                isMissing: resolvedFrequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: 'Avg. Duration',
                valueLabel: avgDurationLabel,
                deltaValue: resolvedAvgDurationDelta,
                deltaLabel: avgDurationDeltaLabel,
                isMissing: resolvedAvgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<SuiteListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnWidths.actions.flex,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => (
      <TableActionsCell
        rowName={params.row.name}
        editTagsEntityNoun="suite"
        rowTags={resolveEntityTags(params.row)}
        rowTagColors={resolveEntityTags(params.row).map((tag) => tagColorByName[tag] ?? 'neutral')}
        onUpdateTagsColor={(input) => {
          onUpdateRowTagsColor?.({ row: params.row, ...input });
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
        updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestSummaryColumn,
    ...(includeLatestTestsColumn ? [testsColumn] : []),
    ...(latestAsMeta ? [executionsColumn] : []),
    ...(latestAsMeta ? [newMetricsColumn] : []),
    ...(includeLastExecutedColumn ? [lastExecutedColumn] : []),
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};
